import React, { useState } from 'react';
import ProviderSelectionStep from './ProviderSelectionStep';
import axiosInstance from '../../utils/axiosConfig';
import { getProviders } from '../../utils/apis';
import { useQuery } from 'react-query';
import { getUserFriendlyErrorMessage } from '../../utils/errorHandler';
import styles from './EditAppointmentRouter.module.css';

interface EditAppointmentRouterProps {
  appointment: any;
  onDone: () => void;
}

const EditAppointmentRouter: React.FC<EditAppointmentRouterProps> = ({ appointment, onDone }) => {
  const [step, setStep] = useState(0);
  const [provider, setProvider] = useState<any>(null);
  const [slot, setSlot] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Determine appointment type and zip
  const appointmentType = appointment.appointmentType === 'In-person' ? 'inperson' : 'telehealth';
  const zipCode = appointment.zipCode;

  // Always call hooks at the top level
  const {
    data: providers = [],
    isLoading: providersLoading,
    error: providersError,
  } = useQuery(
    ['providers', appointmentType, zipCode],
    () => getProviders(appointmentType, zipCode),
    {
      enabled: step === 0 && !!appointmentType && (appointmentType === 'telehealth' || !!zipCode),
    }
  );

  // Step 1: Provider/slot selection
  if (step === 0) {
    return (
      <ProviderSelectionStep
        onBack={onDone}
        onSelect={(prov: any, slot: any) => {
          setProvider(prov);
          setSlot(slot);
          setStep(1);
        }}
        onNewSearch={onDone}
        providers={providers}
        isLoading={providersLoading}
        error={providersError as string | null}
      />
    );
  }

  // Step 2: Confirmation
  if (step === 1) {
    const formatDateTime = (dateTime: string) => {
      const date = new Date(dateTime);
      return date.toLocaleString([], { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true 
      });
    };

    return (
      <div className={styles.editAppointment}>
        {/* Header/Navigation */}
        <div className={styles.navBar}>
          <span className={styles.navPhone}>(720) 400-7025</span>
          <div className={styles.navLinks}>
            <button className={styles.navLink} onClick={() => window.location.reload()}>BOOK ONLINE</button>
          </div>
          <div className={styles.navMenuIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12h18M3 6h18M3 18h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          <div className={styles.confirmationCard}>
            <div className={styles.cardHeader}>
              <svg className={styles.cardIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <div className={styles.cardTitle}>Confirm Appointment Update</div>
            </div>
            
            <div className={styles.cardBody}>
              <p className={styles.cardDescription}>
                Review your new appointment details and confirm the update.
              </p>
              
              <div className={styles.appointmentDetails}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Appointment Type:</span>
                  <span className={styles.detailValue}>
                    {appointmentType === 'telehealth' ? 'Telehealth' : 'In-person'}
                  </span>
                </div>
                {provider && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Provider:</span>
                    <span className={styles.detailValue}>{provider.name}</span>
                  </div>
                )}
                {slot && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Date & Time:</span>
                    <span className={styles.detailValue}>
                      {slot.startTime ? formatDateTime(slot.startTime) : ''}
                    </span>
                  </div>
                )}
                {appointment.patient && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Patient Name:</span>
                    <span className={styles.detailValue}>
                      {appointment.patient.firstName} {appointment.patient.lastName}
                    </span>
                  </div>
                )}
                {appointment.patient && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Email:</span>
                    <span className={styles.detailValue}>{appointment.patient.email}</span>
                  </div>
                )}
              </div>

              {error && <div className={styles.errorMessage}>{error}</div>}
              {success && <div className={styles.successMessage}>Appointment updated successfully!</div>}

              <div className={styles.actionButtons}>
                <button
                  className={styles.updateButton}
                  onClick={async () => {
                    setLoading(true);
                    setError(null);
                    setSuccess(false);
                    try {
                      await axiosInstance.put(
                        `/api/appointments/${appointment.id}`,
                        {
                          patientId: appointment.patient?.id,
                          providerName: provider.name,
                          appointmentType: appointmentType === 'telehealth' ? 'Telehealth' : 'In-person',
                          appointmentDateTime: slot.startTime + 'Z',
                          status: 'BOOKED',
                          zipCode,
                        },
                        {
                          headers: { client: 'axis', domain: 'axis' },
                        }
                      );
                      setSuccess(true);
                      setStep(2);
                    } catch (err: any) {
                      const userFriendlyError = getUserFriendlyErrorMessage(err, 'updating appointment');
                      setError(userFriendlyError);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || success}
                >
                  {loading ? 'Updating...' : 'Update Appointment'}
                </button>
                
                <button
                  className={styles.cancelButton}
                  onClick={onDone}
                  disabled={loading}
                >
                  ← Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
          <div className={styles.footerInfo}>
            <div className={styles.footerInfoItem}>
              <span>📞</span>
              <span>Phone (appointments): 720-400-7025</span>
            </div>
            <div className={styles.footerInfoItem}>
              <span>📍</span>
            </div>
          </div>
          <div className={styles.rating}>
            <span className={styles.star}>★</span>
            <span className={styles.star}>★</span>
            <span className={styles.star}>★</span>
            <span className={styles.star}>★</span>
            <span className={styles.star}>★</span>
          </div>
          <div className={styles.ratingText}>Average Rating: 4.9/5</div>
        </div>

        {/* Accessibility Icon */}
        <div className={styles.accessibilityIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="4" r="2" fill="white"/>
            <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
      </div>
    );
  }

  // Step 3: Success
  return (
    <div className={styles.editAppointment}>
      {/* Header/Navigation */}
      <div className={styles.navBar}>
        <span className={styles.navPhone}>(720) 400-7025</span>
        <div className={styles.navLinks}>
          <button className={styles.navLink} onClick={() => window.location.reload()}>BOOK ONLINE</button>
        </div>
        <div className={styles.navMenuIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.successCard}>
          <div className={styles.successIcon}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#10b981" stroke="#10b981" strokeWidth="2"/>
              <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <h1 className={styles.title}>Appointment Updated!</h1>
          <p className={styles.subtitle}>
            Your appointment has been successfully updated. You will receive a confirmation email shortly.
          </p>
          
          <div className={styles.confirmationDetails}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>What's Next:</span>
              <span className={styles.detailValue}>
                Check your email for updated appointment details.
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Reminder:</span>
              <span className={styles.detailValue}>
                You'll receive a reminder 24 hours before your appointment.
              </span>
            </div>
          </div>

          <div className={styles.actionButtons}>
            <button
              className={styles.primaryButton}
              onClick={onDone}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
        <div className={styles.footerInfo}>
          <div className={styles.footerInfoItem}>
            <span>📞</span>
            <span>Phone (appointments): 720-400-7025</span>
          </div>
          <div className={styles.footerInfoItem}>
            <span>📍</span>
          </div>
        </div>
        <div className={styles.rating}>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
        </div>
        <div className={styles.ratingText}>Average Rating: 4.9/5</div>
      </div>

      {/* Accessibility Icon */}
      <div className={styles.accessibilityIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="4" r="2" fill="white"/>
          <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );
};

export default EditAppointmentRouter; 